/**
 * 取得失敗バナー (= 部分描画の継続) が名指しするデータ源ラベルの写像
 * (index.errorResilienceTestFixtures.tsx `MOUNT_TIME_READ_RPCS` が固定する挙動)。
 *
 * スポット入札画面は独立した複数 read RPC の合成画面で、 各 query は
 * `throwOnError: false` で error boundary (= 全画面エラー) に飛ばさず、
 * 失敗したデータ源だけをバナーで知らせて残りは描画を続ける
 * (`index.tsx` 「取得失敗の集約」セクション)。
 *
 * 会社情報 (`CompanyInfoService.getCompanyInfo`) の取得失敗はこの写像に
 * 含まれない (= バナーで名指ししない、 意図的)。
 */
export interface FailedDataFlags {
  /** 通常入札 承認依頼一覧の取得失敗。 */
  isSpotListError: boolean
  /** 通常入札 / ブロック入札 screen (合成 RPC) の取得失敗。 */
  isScreenError: boolean
  /** screen 内の通常入札ソースだけが失敗扱いになっている状態。 */
  isSpotScreenSourceFailed: boolean
  /** ブロック入札 承認依頼一覧の取得失敗。 */
  isBlockListError: boolean
  /** screen 内のブロック入札ソースだけが失敗扱いになっている状態。 */
  isBlockScreenSourceFailed: boolean
  /** 他受渡日 (受渡日フィルタなし) の承認依頼一覧の取得失敗。 */
  isCrossDateApprovalsError: boolean
  /** 調達必要量の取得失敗。 */
  needUnavailable: boolean
  /** 入札制限の取得失敗。 */
  isBidLimitsError: boolean
  /** 取引可能エリアの取得失敗。 */
  isTradableAreasError: boolean
  /**
   * 業務エリア設定 (個人設定) の取得失敗。 取得できない間は絞り込まない fallback
   * (= 全 9 エリア) になるので、 業務エリア外のエリアも選べることを
   * 利用者へ知らせる (`~/hooks/useBusinessAreaFilter` の `isBusinessAreasError`)。
   */
  isBusinessAreasError: boolean
  /** ワークフローケースの取得失敗。 */
  isWfCaseError: boolean
  /**
   * 承認フロー設定 (`ApprovalFlowService.GetApprovalFlowScreen`) の取得失敗。
   * 取得中 (isLoading) はこのバナーに合流しない (= ボタン非活性のみで足りる、
   * 2026-09-16 ユーザー決定 1-B)。 取得失敗は「時間をおいて再読み込み」 の
   * 案内を他のデータ源と揃えて出す。
   */
  isApprovalFlowError: boolean
}

export interface FailedDataLabels {
  spotBids: string
  blockBids: string
  crossDateApprovals: string
  procurementNeed: string
  bidLimits: string
  tradableAreas: string
  businessAreas: string
  workflowCase: string
  approvalFlow: string
}

/**
 * 失敗フラグの組から、 取得失敗バナーに出すデータ源ラベルの一覧を求める。
 *
 * 1 本の RPC 障害で複数のデータ源が同時に落ちる場合がある
 * (例: 承認依頼一覧は選択受渡日ぶんと横断ぶんの両方を引く) ため、
 * 該当する全ラベルを (重複を許さず、 固定順で) 返す。
 */
export function computeFailedDataLabels(
  flags: FailedDataFlags,
  labels: FailedDataLabels,
): string[] {
  return [
    (flags.isSpotListError ||
      flags.isScreenError ||
      flags.isSpotScreenSourceFailed) &&
      labels.spotBids,
    (flags.isBlockListError ||
      flags.isScreenError ||
      flags.isBlockScreenSourceFailed) &&
      labels.blockBids,
    flags.isCrossDateApprovalsError && labels.crossDateApprovals,
    flags.needUnavailable && labels.procurementNeed,
    flags.isBidLimitsError && labels.bidLimits,
    flags.isTradableAreasError && labels.tradableAreas,
    flags.isBusinessAreasError && labels.businessAreas,
    flags.isWfCaseError && labels.workflowCase,
    flags.isApprovalFlowError && labels.approvalFlow,
  ].filter((label): label is string => typeof label === 'string')
}
